import { useState, useEffect } from 'react';
import { collection, onSnapshot, query, orderBy } from 'firebase/firestore';
import { db } from '../../firebase';
import { Users, TrendingUp, Calendar, Loader2, Target, UserCheck, DollarSign, BookOpen, GraduationCap } from 'lucide-react';
import { AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';

export default function AdminOverview() {
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState({ total: 0, today: 0, thisWeek: 0, interested: 0, customers: 0, revenue: 0 });
  const [chartData, setChartData] = useState([]);
  const [courseStats, setCourseStats] = useState({ total: 0, published: 0, totalStudents: 0 });

  useEffect(() => {
    const q = query(collection(db, 'registrations'), orderBy('createdAt', 'asc'));
    const unsubscribe = onSnapshot(q, (querySnapshot) => {
      const now = new Date();
      const today = new Date(now.getFullYear(), now.getMonth(), now.getDate()).getTime();
      const oneWeekAgo = new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000).getTime();

      let total = 0;
      let todayCount = 0;
      let weekCount = 0;
      let interestedCount = 0;
      let customerCount = 0;
      let totalRevenue = 0;

      // Data structure for the Area Chart (registrations per day)
      const dailyCounts = {};

      querySnapshot.forEach((doc) => {
        const data = doc.data();
        if (!data.createdAt) return;

        const date = data.createdAt.toDate ? data.createdAt.toDate() : new Date(data.createdAt);
        const time = date.getTime();

        total++;

        if (data.status === 'customer') {
          customerCount++;
          totalRevenue += parseFloat(data.amountPaid || 0);
        } else {
          interestedCount++;
        }

        if (time >= today) {
          todayCount++;
        }
        if (time >= oneWeekAgo) {
          weekCount++;
        }

        // Format date for chart
        const dateStr = date.toLocaleDateString('ar-EG', { month: 'short', day: 'numeric' });
        dailyCounts[dateStr] = (dailyCounts[dateStr] || 0) + 1;
      });

      setStats({ total, today: todayCount, thisWeek: weekCount, interested: interestedCount, customers: customerCount, revenue: totalRevenue });

      // Convert the map to an array for the chart
      const formattedChartData = Object.keys(dailyCounts).map(date => ({
        date,
        المسجلين: dailyCounts[date]
      }));

      if (formattedChartData.length === 0) {
        setChartData([
          { date: 'قبل 4 أيام', المسجلين: 0 },
          { date: 'قبل 3 أيام', المسجلين: 2 },
          { date: 'أمس', المسجلين: 1 },
          { date: 'اليوم', المسجلين: 5 },
        ]);
      } else {
        setChartData(formattedChartData);
      }

      setLoading(false);
    }, (error) => {
      console.error("Error fetching admin stats", error);
      setLoading(false);
    });

    return () => unsubscribe();
  }, []);

  // ── Courses stats listener ──────────────────────────────────────────────
  useEffect(() => {
    const unsub = onSnapshot(collection(db, 'courses'), (snap) => {
      let total = 0, published = 0, totalStudents = 0;
      snap.forEach(d => {
        total++;
        if (d.data().status === 'published') published++;
        totalStudents += d.data().studentsCount || 0;
      });
      setCourseStats({ total, published, totalStudents });
    });
    return () => unsub();
  }, []);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-full min-h-[400px]">
        <Loader2 className="w-10 h-10 animate-spin text-primary-600" />
      </div>
    );
  }

  return (
    <div className="animate-in fade-in slide-in-from-bottom-4 duration-500">
      <h1 className="text-3xl font-extrabold text-slate-900 dark:text-white mb-2">نظرة عامة</h1>
      <p className="text-xl dark:text-zinc-400 mb-8">مرحباً بك في لوحة تحكم لباب سكول. إليك ملخص الأداء.</p>

      {/* Metrics Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 mb-8">

        {/* Revenue Card */}
        <div className="bg-gradient-to-l from-primary-600 to-primary-800 border border-primary-500/30 p-6 rounded-3xl shadow-lg flex items-center justify-between sm:col-span-2 lg:col-span-3">
          <div>
            <p className="text-lg font-bold text-white/80 mb-1">إجمالي الإيرادات (المشتريات)</p>
            <p className="text-5xl font-extrabold text-white">${stats.revenue}</p>
          </div>
          <div className="w-16 h-16 rounded-2xl bg-white/10 text-white flex items-center justify-center backdrop-blur-md">
            <DollarSign className="w-8 h-8" />
          </div>
        </div>

        <div className="bg-white dark:bg-zinc-900 border border-slate-200 dark:border-white/10 p-6 rounded-3xl shadow-sm flex items-center justify-between">
          <div>
            <p className="text-lg font-bold text-slate-500 dark:text-zinc-400 mb-1">العملاء الحقيقيين</p>
            <p className="text-4xl font-extrabold text-slate-900 dark:text-white">{stats.customers}</p>
          </div>
          <div className="w-14 h-14 rounded-2xl bg-emerald-50 dark:bg-emerald-500/10 text-emerald-600 dark:text-emerald-500 flex items-center justify-center">
            <UserCheck className="w-7 h-7" />
          </div>
        </div>

        <div className="bg-white dark:bg-zinc-900 border border-slate-200 dark:border-white/10 p-6 rounded-3xl shadow-sm flex items-center justify-between">
          <div>
            <p className="text-lg font-bold text-slate-500 dark:text-zinc-400 mb-1">العملاء المهتمين</p>
            <p className="text-4xl font-extrabold text-slate-900 dark:text-white">{stats.interested}</p>
          </div>
          <div className="w-14 h-14 rounded-2xl bg-amber-50 dark:bg-amber-500/10 text-amber-600 dark:text-amber-500 flex items-center justify-center">
            <Target className="w-7 h-7" />
          </div>
        </div>

        <div className="bg-white dark:bg-zinc-900 border border-slate-200 dark:border-white/10 p-6 rounded-3xl shadow-sm flex items-center justify-between">
          <div>
            <p className="text-lg font-bold text-slate-500 dark:text-zinc-400 mb-1">إجمالي المسجلين</p>
            <p className="text-4xl font-extrabold text-slate-900 dark:text-white">{stats.total}</p>
          </div>
          <div className="w-14 h-14 rounded-2xl bg-primary-50 dark:bg-primary-500/10 text-primary-600 dark:text-primary-500 flex items-center justify-center">
            <Users className="w-7 h-7" />
          </div>
        </div>

        <div className="bg-white dark:bg-zinc-900 border border-slate-200 dark:border-white/10 p-6 rounded-3xl shadow-sm flex items-center justify-between">
          <div>
            <p className="text-lg font-bold text-slate-500 dark:text-zinc-400 mb-1">مسجلي هذا الأسبوع</p>
            <p className="text-4xl font-extrabold text-slate-900 dark:text-white">{stats.thisWeek}</p>
          </div>
          <div className="w-14 h-14 rounded-2xl bg-green-50 dark:bg-green-500/10 text-green-600 dark:text-green-500 flex items-center justify-center">
            <TrendingUp className="w-7 h-7" />
          </div>
        </div>

        <div className="bg-white dark:bg-zinc-900 border border-slate-200 dark:border-white/10 p-6 rounded-3xl shadow-sm flex items-center justify-between">
          <div>
            <p className="text-lg font-bold text-slate-500 dark:text-zinc-400 mb-1">مسجلي اليوم</p>
            <p className="text-4xl font-extrabold text-slate-900 dark:text-white">{stats.today}</p>
          </div>
          <div className="w-14 h-14 rounded-2xl bg-blue-50 dark:bg-blue-500/10 text-blue-600 dark:text-blue-500 flex items-center justify-center">
            <Calendar className="w-7 h-7" />
          </div>
        </div>

      </div>

      {/* ── Course Statistics ─────────────────────────────────────────────── */}
      <div className="mb-8">
        <h2 className="text-xl font-extrabold text-slate-900 dark:text-white mb-4 flex items-center gap-2">
          <BookOpen className="w-5 h-5 text-primary-600" />
          إحصائيات الدورات
        </h2>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-6">
          <div className="bg-white dark:bg-zinc-900 border border-slate-200 dark:border-white/10 p-6 rounded-3xl shadow-sm flex items-center justify-between">
            <div>
              <p className="text-lg font-bold text-slate-500 dark:text-zinc-400 mb-1">إجمالي الدورات</p>
              <p className="text-4xl font-extrabold text-slate-900 dark:text-white">{courseStats.total}</p>
            </div>
            <div className="w-14 h-14 rounded-2xl bg-indigo-50 dark:bg-indigo-500/10 text-indigo-600 dark:text-indigo-400 flex items-center justify-center">
              <BookOpen className="w-7 h-7" />
            </div>
          </div>

          <div className="bg-white dark:bg-zinc-900 border border-slate-200 dark:border-white/10 p-6 rounded-3xl shadow-sm flex items-center justify-between">
            <div>
              <p className="text-lg font-bold text-slate-500 dark:text-zinc-400 mb-1">الدورات المنشورة</p>
              <p className="text-4xl font-extrabold text-slate-900 dark:text-white">{courseStats.published}</p>
            </div>
            <div className="w-14 h-14 rounded-2xl bg-emerald-50 dark:bg-emerald-500/10 text-emerald-600 dark:text-emerald-500 flex items-center justify-center">
              <GraduationCap className="w-7 h-7" />
            </div>
          </div>

          <div className="bg-white dark:bg-zinc-900 border border-slate-200 dark:border-white/10 p-6 rounded-3xl shadow-sm flex items-center justify-between">
            <div>
              <p className="text-lg font-bold text-slate-500 dark:text-zinc-400 mb-1">مجموع الطلاب المسجّلين</p>
              <p className="text-4xl font-extrabold text-slate-900 dark:text-white">{courseStats.totalStudents}</p>
            </div>
            <div className="w-14 h-14 rounded-2xl bg-violet-50 dark:bg-violet-500/10 text-violet-600 dark:text-violet-500 flex items-center justify-center">
              <Users className="w-7 h-7" />
            </div>
          </div>
        </div>
      </div>

      {/* Chart Section */}
      <div className="bg-white dark:bg-zinc-900 border border-slate-200 dark:border-white/10 rounded-3xl p-6 md:p-8 shadow-sm">
        <h3 className="text-xl font-bold text-slate-900 dark:text-white mb-6">تدفق المسجلين</h3>
        <div className="h-[400px] w-full" dir="ltr">
          <ResponsiveContainer width="100%" height="100%">
            <AreaChart data={chartData} margin={{ top: 10, right: 10, left: 0, bottom: 0 }}>
              <defs>
                <linearGradient id="colorRegistrations" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%" stopColor="#e00000" stopOpacity={0.3} />
                  <stop offset="95%" stopColor="#e00000" stopOpacity={0} />
                </linearGradient>
              </defs>
              <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#334155" opacity={0.2} />
              <XAxis dataKey="date" axisLine={false} tickLine={false} tick={{ fill: '#64748b', fontSize: 14, fontWeight: 'bold' }} dy={10} />
              <YAxis axisLine={false} tickLine={false} tick={{ fill: '#64748b', fontSize: 14, fontWeight: 'bold' }} dx={-10} allowDecimals={false} />
              <Tooltip
                contentStyle={{ borderRadius: '1rem', border: 'none', boxShadow: '0 10px 25px -5px rgba(0, 0, 0, 0.1), 0 8px 10px -6px rgba(0, 0, 0, 0.1)', background: 'rgba(255,255,255,0.9)', backdropFilter: 'blur(8px)', direction: 'rtl' }}
                itemStyle={{ color: '#0f172a', fontWeight: 'bold' }}
                labelStyle={{ color: '#64748b', fontWeight: 'bold', marginBottom: '0.5rem' }}
              />
              <Area type="monotone" dataKey="المسجلين" stroke="#e00000" strokeWidth={4} fillOpacity={1} fill="url(#colorRegistrations)" activeDot={{ r: 8, strokeWidth: 0, fill: '#e00000' }} />
            </AreaChart>
          </ResponsiveContainer>
        </div>
      </div>
    </div>
  );
}
